/**
 * @ngdoc Service
 */
import {sprintf} from '@/angular/libs/sprintf';
import Vue from 'vue';
import { vrmInstallationAccessLevel } from '@/vue/store/modules/installation-access-level';
import { InstallationTank } from '@/vue/helpers/installation/tank';
import { propRead } from '@/vue/helpers/handleInstallationsValues';
import camelCase from 'camel-case';

(function() {
    'use strict';

    angular.module('vrm.core')
        .service('InstallationsService', InstallationsService)
        .constant('vrmInstallationAccessLevel', vrmInstallationAccessLevel);

    /**
     * @constructor
     * @ngInject
     */
    function InstallationsService(MQTTRealtimeService, $log, $q, $rootScope, $timeout, ApiService, $state, vrmConfig, vrmNotificationsService, vrmNotificationType, locale, localStorageService, UserService) {
        MQTTRealtimeService.setHost(vrmConfig.mqttHostRealtime);
        var realtimeInstallation = null;
        let realtimeDisabled = false;
        let isInstallationFirstLoad = true;

        /* Public API */
        var service = Vue.observable({
            installations: [],
            installation: null,
            _activeInstallation: null,
            get activeInstallation() {
                return this._activeInstallation;
            },
            set activeInstallation(value) {
                this._activeInstallation = value;
                $rootScope.$emit('activeInstallation:changed');
            },
            setRealtimeInstallation: setRealtimeInstallation,
            tags: [],
            alarm_tags: [],
            loadingNavBar: getNavBarInstallations(),
            loading: getInstallations(),
            reload: reload,
            getInstallation: getInstallation,
            canMaintainRealtimeCommunication: canMaintainRealtimeCommunication,
            redirectUnauthorizedUser: redirectUnauthorizedUser,
            getSingleInstallationId: getSingleInstallationId,
            redirectToInstallation: redirectToInstallation,
        });

        function canMaintainRealtimeCommunication() {
            return (service.activeInstallation.view_permissions.twoway || service.activeInstallation.view_permissions.readonly_realtime) &&
            service.activeInstallation.realtimeUpdates &&
            !realtimeDisabled;
        }

        var redirectedToAddSite = false;

        function getNavBarInstallations () {
            return ApiService.users('navbar-sites?extended=1', [])
                .then(function (response) {
                    if(response.data.success){
                        response.data.data.forEach(newSite =>{
                            _parseInstallation(newSite);
                            if (service.installations.findIndex(site => site.id === newSite.id) === -1) {
                                // only add if the site doesn't exist
                                service.installations.push(newSite);
                            }
                        })
                        $rootScope.$emit('installations:updated');
                    }
                    return true;
                }, function() {
                    return false;
                })
        }

        /**
         * Fetch initial dataset for installations
         */
        function getInstallations() {
            return ApiService.users('installations?extended=1', []).then(async function (response) {
                if (response.data.success) {
                    // parse out all unique tags
                    _parseTags(response.data.records);
                    await locale.ready('alarms');

                    const newSites = response.data.records;

                    const BATCH_SIZE = 100;
                    async function loadInstallations(newSites) {
                        const promise = new Promise((resolve, reject) => {
                            const batch = newSites.slice(0, BATCH_SIZE);
                            const rest = newSites.slice(BATCH_SIZE);

                            batch.forEach(newSite => {
                                _parseInstallation(newSite);
                                const index = service.installations.findIndex(site => site.id === newSite.id);
                                if (index >= 0) {
                                    service.installations[index] = newSite;
                                } else {
                                    service.installations.push(newSite);
                                }
                            });
                            $rootScope.$emit('installations:updated');

                            $timeout(() => resolve(rest), 50);
                        }).then(rest => {
                            if (rest.length > 0) {
                                return loadInstallations(rest);
                            }
                        });
                        return promise;
                    };

                    function reloadInstallations(newSites) {
                        // parse installation data
                        service.installations.forEach(site => site.__markedForTrash = true);

                        /** @type {Array<*>} */
                        newSites.forEach(newSite => {
                            _parseInstallation(newSite);

                            const index = service.installations.findIndex(site => site.id === newSite.id);
                            if (index >= 0) {
                                service.installations.splice(index, 1, newSite);
                            } else {
                                service.installations.push(newSite);
                            }
                            newSite.__markedForTrash = false;
                        });

                        for (let index = 0; index < service.installations.length;) {
                            const site = service.installations[index];
                            if (site.__markedForTrash) {
                                service.installations.splice(index, 1);
                            } else {
                                index++;
                            }
                            delete site.__markedForTrash;
                        }
                    }

                    async function loadOrUpdateInstallations(newSites) {
                        return new Promise((resolve, reject) => {
                            if (isInstallationFirstLoad || service.installations.length === 0) {
                                isInstallationFirstLoad = false;
                                loadInstallations(newSites).then(() => resolve());
                            } else {
                                reloadInstallations(newSites);
                                resolve();
                            }
                        });
                    }

                    loadOrUpdateInstallations(newSites).then(() => {
                        if (service.installations.length === 0) {
                            // If a user has no installations, redirect to add installations view once
                            if ($rootScope.userState && !$rootScope.userState.isShare && !$rootScope.userState.isDemo && !redirectedToAddSite) {
                                if ($state.current.name.indexOf('add-installation') < 0)
                                    $state.go('base.add-installation');

                                redirectedToAddSite = true;
                            }
                        }

                        if (service.installations.length === 1) {
                            localStorageService.set(getSingleInstallationStorageKey(), service.installations[0].id);
                        } else {
                            localStorageService.remove(getSingleInstallationStorageKey());
                        }

                        // Store the site count for the signed-in user so we can easily redirect to the fleet-monitor
                        // on subsequent logins to this account.
                        UserService.setSiteCountToLocalStorage(service.installations.length || 0);

                        $rootScope.$emit('installations:updated');
                    });
                }
                return true;
            }, function() {
                service.installations.length = 0;
                service.installations = [];
                service.tags.length = 0;
                service.tags = [];
                return false;
            });
        }

        function getSingleInstallationStorageKey() {
            const userId = ApiService.getUserId();
            return userId && `${userId}.vrm_single_installation`;
        }

        function getSingleInstallationId() {
            const storageKey = getSingleInstallationStorageKey();
            return storageKey && localStorageService.get(storageKey);
        }


        /**
         * Get installation
         * @param id
         * @param force
         * @return installation object
         */
        function getInstallation(id, force) {
            return $q.when(service.loadingNavBar, function() {
                var installation = _.find(service.installations, {idSite: id});

                //Check if installation is already been loading
                if (installation == null || !installation.view_permissions) {
                    if (!service.installation || service.installation.id != id || force) {
                        service.installation = {
                            id: id,
                            promise: ApiService.users('installations?extended=1&idSite=' + id, []).then(function (response) {
                                if (typeof response.data.records[0] !== "undefined") {
                                    let installation = response.data.records[0];
                                    _parseInstallation(installation);
                                    service.activeInstallation = installation;
                                    const index = service.installations.findIndex(site => site.id === installation.id);
                                    if (index >= 0) {
                                        service.installations.splice(index, 1, installation);
                                    } else {
                                        service.installations.push(installation);
                                    }
                                    return installation;
                                }
                                else {
                                    locale.ready('general').then(function() {
                                        vrmNotificationsService.add({type: vrmNotificationType.ERROR, message: locale.getString('general.installation_access_denied')}, true);
                                        $state.go('base.installation-overview');
                                    });

                                }
                                return null;
                            })
                        };
                    }
                    else if (service.installation.id === id) {
                        service.installation.promise.then(function (response) {
                            service.activeInstallation = response;
                        })
                    }
                    return service.installation.promise;
                } else {
                    service.activeInstallation = installation;
                    return installation;
                }
            });
        }

        /**
         *
         * @param installations
         */
        function _parseTags(installations) {
            var allTags = [];

            var selectedTags = _.filter(service.tags, function (tag) {
                return tag.selected == true;
            });
            // iterate all tags for installations, there will be duplicates
            _.each(installations, function (installation) {
                _.each(installation.tags, function (tag) {
                    var selected = _.find(selectedTags, function (curTag) {
                        return curTag.idTag == tag.idTag;
                    });
                    allTags.push({idTag: tag.idTag, selected: !!selected, label: tag.name, source: tag.source});
                });
            });
            // reduce to unique
            var uniqueTags = _.uniqWith(allTags, function (a, b) {
                return a.idTag == b.idTag;
            });

            // clear model
            service.tags.length = 0;

            // add to model
            _.each(uniqueTags, function (tag) {
                service.tags.push(tag);
            });
        }

        function _parseAlarms(installation) {
            if (!installation.current_alarms || !installation.current_alarms.length) {
                return;
            }

            installation.alarm_tags = [];
            installation.current_alarms_formatted = [];
            installation.current_alarms.forEach(function (alarm, index) {
                const device = alarm.device || '';
                const customName = alarm.customName ? ` - ${alarm.customName} ` : '';
                const instance = (!alarm.instance || !alarm.device || alarm.instance === 0) ? '' : ` [${alarm.instance}]`;
                const separator = device ? ' - ' : '';
                const description = alarm.description || '';
                const nameEnum = alarm.nameEnum ? `: ${alarm.nameEnum}` : '';

                // Create tags for installations alarms
                let alarmTagLabel = device ? `${device}: ${description}` : description;
                if (alarmTagLabel.includes('No data alarm')) {
                    alarmTagLabel = alarmTagLabel.split(',')[0];
                }
                if (!service.alarm_tags.includes(alarmTagLabel)) {
                    service.alarm_tags.push(alarmTagLabel)
                }
                if (!installation.alarm_tags.includes(alarmTagLabel)) {
                    installation.alarm_tags.push(alarmTagLabel);
                }

                // Installation alarm translation
                const alarmMessage = `${device}${customName}${instance}${separator}${description}${nameEnum}`;
                if (alarmMessage.includes('No data alarm')) {
                    installation.current_alarms_formatted[index] =
                        locale.getString('alarms.messages.no_data', null, true) ||
                        "This site has stopped reporting to VRM. Check the internet connection to the GX device.";
                    return;
                }
                const termKey = camelCase(alarm.type);
                installation.current_alarms_formatted[index] = locale.getString(`alarms.messages.${termKey}`, null, true) || alarmMessage;
            });
        }

        function _parseInstallation(installation) {
            installation.data = {};
            installation.id = installation.idSite;
            let isOffsetNegative = !(installation.timezone_offset >= 0);
            let offsetWithoutSign = (isOffsetNegative ?
                parseInt(installation.timezone_offset.toString().substring(1)) : installation.timezone_offset
            );

            installation.timezone_offset_textual =
                (isOffsetNegative ? "-" : "+") +
                Math.floor(offsetWithoutSign / 3600)
                    .toString()
                    .padStart(2, "0") +
                ((offsetWithoutSign % 3600) / 60)
                    .toString()
                    .padStart(2, "0");
            // create referenced list by data attribute code
            _.each(installation.extended, function (dataAttribute, index) {
                installation.data[installation.extended[index].code] = installation.extended[index];
            });

            const st = typeof installation.data.st !== 'undefined' && (installation.data.st.rawValue === 'ESS' || installation.data.st.rawValue === 'Hub-4');
            const aM = typeof installation.data.aM !== 'undefined';

            const dem = typeof installation.data.dem !== 'undefined' && +installation.data.dem.rawValue === 1;
            const dea = typeof installation.data.dea !== 'undefined' && +installation.data.dea.rawValue === 1;

            // ESS uses st; AC System ESS uses aM;
            installation.isEssTypeSystem = st || aM;

            // Dynamic ESS installation
            installation.dynamicEssAvailable = dea;
            installation.dynamicEssEnabled = dem;

            // Parse alarms
            _parseAlarms(installation);

            // MQTT
            if (realtimeInstallation === parseInt(installation.id))
                enableMqtt(installation);

            // Installation status
            Object.defineProperty(installation, 'status', {
                get() {
                    const serviceCountDown = propRead(installation, 'gasd', 'rawValue');
                    const serviceInterval = propRead(installation, 'gasi', 'rawValue');
                    const hasServiceInterval = serviceInterval && serviceInterval > 0;
                    const serviceNeeded = propRead(installation, 'gasr') === 'Warning';

                    const { tl, tf } = installation.data;

                    const hasServiceWarning = hasServiceInterval && serviceCountDown <= 0 && serviceNeeded;
                    const hasTankWarning =
                        (tl && tf && tl.instances) &&
                        Object.keys(tl.instances).some(instanceId => {
                            const tank = new InstallationTank(
                                { tl, tf },
                                instanceId
                            );
                            return tank.hasWarning;
                        });

                    if (installation.alarm) {
                        return 'alarm';
                    } else if (hasServiceWarning || hasTankWarning) {
                        return 'warning';
                    }
                    return 'ok';
                }
            });
        }

        /**
         * Removes the current realtime installation and sets a new one if provided
         * @param installation A valid installation to set or null to remove the realtime installation
         */
        function setRealtimeInstallation(installation) {
            if (installation && realtimeInstallation === parseInt(installation.id)) {
                return;
            }
            if (realtimeInstallation) {
                const currentInstallation = _.find(service.installations, {
                    idSite: realtimeInstallation
                });
                if (currentInstallation) {
                    disableMqtt(currentInstallation);
                }
            }
            realtimeInstallation = null;
            if (installation && installation.realtimeUpdates) {
                realtimeInstallation = parseInt(installation.id);
                // Enable realtime updates, but can be disabled from enableMqtt() on high workloads
                realtimeDisabled = false;
                enableMqtt(installation);
            } else {
                realtimeDisabled = true;
            }
        }


        function enableMqtt(installation) {
            if (!installation.view_permissions.twoway && !installation.view_permissions.readonly_realtime) {
                $log.warn('Installation has two way communication disabled, skipping MQTT connection')
                return;
            }
            MQTTRealtimeService.setHost(installation.mqtt_webhost);
            MQTTRealtimeService.setPortalId(installation.identifier);
            if (!installation.extended || !vrmConfig.enableRealtime) {
                return;
            }
            if (installation.high_workload) {
                if (!realtimeDisabled) {
                    locale.ready('general').then(function() {
                        vrmNotificationsService.add({
                            type: vrmNotificationType.WARNING,
                            message: locale.getString('general.installation_overloaded_disabling_mqtt')
                        });
                    });
                }
                realtimeDisabled = true;
                disableMqtt(installation);
                MQTTRealtimeService.cancelRealtimeHandler(installation.identifier);
            } else {
                MQTTRealtimeService.initRealtimeHandler(installation.identifier);
                realtimeDisabled = false;
            }
        }

        function disableMqtt(installation) {
            MQTTRealtimeService.cancelRealtimeHandler(installation.identifier);
            // Setting host to null will reset and disconnect the MQTT service, reverse off enableMqtt
            MQTTRealtimeService.setHost(vrmConfig.mqttHost);
        }

        function reload() {
            if(service.installations.length === 0) {
                // only calls the Nav bar API if the there aren't any installations loaded
                service.loadingNavBar = getNavBarInstallations();
            }
            service.loading = getInstallations();
            if (service.activeInstallation) {
                const currentIdSite = service.activeInstallation.idSite;
                if ($rootScope.userState.isAdmin) {
                    // Admins can request any installation, so force a fetch
                    getInstallation(currentIdSite, true);
                } else {
                    // Update activeInstallation after retrieving available sites
                    service.loading.then(() => {
                        service.activeInstallation = service.installations.find(site => site.idSite === currentIdSite);
                        // If site was not found, the user is viewing the wrong installation
                        if (!service.activeInstallation) {
                            $state.go('base.installation-overview');
                        }
                    });
                }
            }
        }

        // If the user has access to this installation => send to dashboard; else => send to installation-overview
        function redirectUnauthorizedUser(installation) {
            if (installation.view_permissions.settings) {
                $state.go('base.installation.dashboard', {id: installation.id});
            } else {
                $state.go('base.installation-overview');
            }
            locale.ready('common').then(function() {
                vrmNotificationsService.add({
                    type: vrmNotificationType.WARNING,
                    message: locale.getString('common.unauthorized_user')
                });
            });
        }

        // Todo: find out if we can re-use redirectUnauthorizedUser instead
        function redirectToInstallation(installation) {
            if (installation.view_permissions.settings) {
                $state.go('base.installation.dashboard', {id: installation.id});
            } else {
                $state.go('base.installation-overview');
            }
        }

        /* Private */
        $rootScope.$on('logout', function() {
            //Clear installations on login
            service.installations.length = 0;
            service.activeInstallation = null;
            redirectedToAddSite = false;
            setRealtimeInstallation(null);
        });

        $rootScope.$on('login', function() {
            //Reload data on login
            reload();
        });

        $rootScope.$on("page-visibility-change", function (_, isVisible) {
            if (isVisible) {
                realtimeInstallation = null;
                setRealtimeInstallation(service.activeInstallation);
            }
        });

        $rootScope.$on('installation:delete', function() {
            reload();
        });

        return service;
    }
})();
